<template>
  <div
    class="scene-group"
    v-if="dimensions"
    :class="{
      '-selected': selected,
      '-has-selection': selectionInside,
      '-highlighted': highlighted
    }"
    :style="{
      width: dimensions.width + 'px',
      height: dimensions.height + 'px',
      left: dimensions.x + 'px',
      top: dimensions.y + 'px'
    }"
  >
    <slot></slot>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject } from "vue";
import {
  keyItemSizes
} from "@editor/net/netCanvasInjectionKeys";
import { useSceneTree } from "../scene/sceneTree";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { Uid } from "@/helpers/utility/Id";
import type { SceneTreeNode } from "../scene/SceneTreeItem";

const props = defineProps<{
  nodeUid: Uid<"sceneNode">;
  selection: CanvasSelection;
}>();

const sceneTree = useSceneTree();
const itemSizes = inject(keyItemSizes);
const treeNode = computed(() =>
  sceneTree.treeNodes.get(props.nodeUid)
);

const selected = computed(() => {
  if (!treeNode.value) return false;
  return props.selection.selected.has(props.nodeUid);
});
const selectionInside = computed(() => {
  for (const node of allNested.value) {
    if (props.selection.selected.has(node.uid)) {
      return true;
    }
  }
});
const highlighted = computed(() => {
  if (!treeNode.value) return false;
  return props.selection.highlighted.has(props.nodeUid);
});

const allNested = computed((): SceneTreeNode[] => {
  const result: SceneTreeNode[] = [];
  function collectNodes(node: SceneTreeNode) {
    result.push(node);
    node.children.forEach(collectNodes);
  }
  if (treeNode.value) {
    collectNodes(treeNode.value);
  }
  return result;
});

const dimensions = computed(() => {
  if (!treeNode.value) return { width: 0, height: 0, left: 0, top: 0, x: 0, y: 0 };
  return getGroupDimensions(treeNode.value);
});

function getGroupDimensions(node: SceneTreeNode): {
  width: number;
  height: number;
  left: number;
  top: number;
  x: number;
  y: number;
} {
  const childNodes = node.children;
  if (childNodes.length === 0) {
    return { width: 0, height: 0, left: 0, top: 0, x: 0, y: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  childNodes.forEach((childNode) => {
    if (!childNode) return;
    const childSceneNode = childNode.node;
    if (childSceneNode.dataType == "group") {
      const dimensions = getGroupDimensions(childNode);
      if (dimensions.left < minX) minX = dimensions.left;
      if (dimensions.top < minY) minY = dimensions.top;
      if (dimensions.left + dimensions.width > maxX)
        maxX = dimensions.left + dimensions.width;
      if (dimensions.top + dimensions.height > maxY)
        maxY = dimensions.top + dimensions.height;
    } else {
      const size =
        itemSizes?.value[childNode.uid] ||
        childSceneNode;
      const { x, y, width, height } = size;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x + width > maxX) maxX = x + width;
      if (y + height > maxY) maxY = y + height;
    }
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    left: minX,
    top: minY
  };
}
</script>

<style scoped lang="scss">
.scene-group {
  position: absolute;

  &.-highlighted {
    outline: 1px dotted var(--color-primary);
  }
  &.-has-selection {
    outline: 2px dashed var(--color-primary);
  }
  &.-selected {
    outline: 2px solid var(--color-primary);
  }
}
</style>
